import Plus from "@components/icones/plus.tsx";
import { deleteProfile, getProfileList } from "@core/actions/usersActions.ts";
import {
  loadingDeleteProfile,
  loadingProfile,
  profileList,
  profilResponse,
  userConnected,
} from "@core/slice/UsersSlice.ts";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, SortOrder } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import React from "react";
import { ProfilDTO } from "../profil.model.ts";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import Edit from "@components/icones/edit.tsx";
import trash from "@assets/images/trash.png";
import { useNavigate } from "react-router-dom";
import { FetchStatusType } from "@core/modeles/model.ts";
import Spinner from "@shared/Spinner.tsx";
import Message from "@shared/message/Message.tsx";
import CustomDialog from "@shared/customDialog.tsx";
import { TypeProfil } from "@modules/plaintes/plaintes.model.ts";
import { classNames } from "primereact/utils";

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const listProfil = useAppSelector(profileList);
  const loading = useAppSelector(loadingProfile);
  const loadingDelete = useAppSelector(loadingDeleteProfile);
  const profileResponse = useAppSelector(profilResponse);
  const detailsUserConnected = useAppSelector(userConnected);

  const [profilId, setProfilId] = React.useState<string>("");
  const [search, setSearch] = React.useState<string>("");
  const [visibleConfirmDialog, setVisibleConfirmDialog] =
    React.useState<boolean>(false);
  const [open, setOpen] = React.useState(false);
  const [messageInfo, setMessageInfo] = React.useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({
    severity: "success",
    summary: "",
    detail: "",
  });
  const [currentPage, setCurrentPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [order, setOrder] = React.useState<string>("name");
  const [orderDir, setOrderDir] = React.useState<SortOrder>(-1);
  const isCompetentEntity =
    detailsUserConnected?.profil.type === TypeProfil.COMPETENT_ENTITY;

  let orderDirection: string =
    orderDir === 1 ? "asc" : orderDir === -1 ? "desc" : "";

  React.useEffect(() => {
    dispatch(
      getProfileList({
        page: 1,
        size: 10,
        search: "",
        sortBy: "name",
        sortDir: "desc",
      })
    );
  }, [dispatch]);

  const showSuccessMessage = (user: any) => {
    setMessageInfo({
      severity: "success",
      summary: "Succès",
      detail: `Suppression du groupe réussie!`,
    });
    setOpen(true);
  };

  const showErrorMessage = (message?: string) => {
    setMessageInfo({
      severity: "error",
      summary: "Erreur",
      detail: message ? message : "Une erreur c'est produite , veuillez réessayer!",
    });
    setOpen(true);
  };

  const onPageChange = (e: any) => {
    setCurrentPage(e.page ?? 0);
    setPageSize(e.rows);
    dispatch(
      getProfileList({
        page: (e.page ?? 0) + 1,
        size: e.rows,
        sortBy: order,
        sortDir: orderDirection,
        search: search,
      })
    );
  };

  const confirmDelete = () => {
    if (profilId) {
      dispatch(
        deleteProfile({
          id: profilId,
          onError: (err) => {
            showErrorMessage(err.message);
          },
          onSuccess: () => {
            setVisibleConfirmDialog(false);
            showSuccessMessage(profilId);
            dispatch(
              getProfileList({
                page: currentPage + 1,
                size: pageSize,
                search: search,
                sortBy: order,
                sortDir: orderDirection,
              })
            );
          },
        })
      );
    }
  };
  const paginatorTemplate = {
    layout:
      "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown ",
    CurrentPageReport: (options: any) => {
      return (
        <span>
          Afficher {options.first} de {options.last} à {options.totalRecords}{" "}
          lignes
        </span>
      );
    },
  };

  const rejectDelete = () => {
    setVisibleConfirmDialog(false);
  };

  const addProfile = () => navigate("/setting/group/add-group");

  const nameBodyTemplate = (rowData: ProfilDTO) => {
    return `${rowData.name}`;
  };
  const permissionBodyTemplate = (rowData: ProfilDTO) => {
    return `${rowData.permissions.map((perm) => perm.description).join(", ")}`;
  };
  const accessesBodyTemplate = (rowData: ProfilDTO) => {
    return `${rowData.accesses?.map((perm) => perm.description).join(", ")}`;
  };
  const handleDetail = (id: string) => {
    navigate(`/setting/group-detail/${id}`);
  };
  const handleEdit = (id: string) => {
    navigate(`/setting/group/add-group/${id}`);
  };

  const handleSearch = () => {
    dispatch(
      getProfileList({
        page: currentPage + 1,
        size: pageSize,
        search: search,
        sortBy: order,
        sortDir: orderDirection,
      })
    );
  };

  const actionBodyTemplate = (rowData: ProfilDTO) => (
    <div className="flex items-center space-x-2 gap-2">
      <Button
        tooltip="Voir"
        tooltipOptions={{ position: "left" }}
        onClick={() => handleDetail(rowData.id)}
      >
        <MdOutlineRemoveRedEye
          color="var(--primary-color)"
          style={{ width: "24px", height: "24px" }}
        />
      </Button>

      <Button
        tooltip={
          !isCompetentEntity
            ? "Modifier"
            : "Action réservée aux administrateurs"
        }
        disabled={isCompetentEntity}
        tooltipOptions={{ position: "left", showOnDisabled: true }}
        onClick={() => handleEdit(rowData.id)}
      >
        <Edit />
      </Button>

      <Button
        disabled={isCompetentEntity}
        tooltip={
          !isCompetentEntity
            ? "Supprimer"
            : "Action réservée aux administrateurs"
        }
        tooltipOptions={{ position: "left", showOnDisabled: true }}
        onClick={() => {
          setVisibleConfirmDialog(true);
          setProfilId(rowData.id);
        }}
      >
        <img src={trash} alt="Supprimer" className="h-auto" />
      </Button>
    </div>
  );
  return (
    <>
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />
      <CustomDialog
        visible={visibleConfirmDialog}
        title="Voulez-vous vraiment supprimer ce groupe?"
        rejectLabel="Annuler"
        confirmLabel="Supprimer"
        confirmDialog={confirmDelete}
        rejectDialog={rejectDelete}
        loading={loadingDelete === FetchStatusType.LOADING}
        disabled={loadingDelete === FetchStatusType.LOADING}
      />
      <div className="w-full flex flex-col">
        <div className="p-4 flex justify-end">
          <Button
            className={classNames(
              "btn-primary bg-green-500 float-right hover:!bg-green-900 text-white font-bold py-2 px-4 rounded-full gap-2",
              { "btn-primary-disabled": isCompetentEntity }
            )}
            onClick={addProfile}
            disabled={isCompetentEntity}
            tooltip={
              !isCompetentEntity ? "" : "Action réservée aux administrateurs"
            }
            tooltipOptions={{ position: "left", showOnDisabled: true }}
          >
            <Plus />
            <span>Ajouter un groupe</span>
          </Button>
        </div>

        <div className="bg-white space-y-4 card-container pb-6">
          <div className=" px-8 lg:pl-8 pt-8 grid-cols-5">
            <div className="flex items-center w-full md:w-full lg:w-1/4">
              <InputText
                placeholder="Rechercher un groupe"
                className="flex-1 p-2 outline-none border-none"
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
              <Button
                icon="pi pi-search"
                className="p-[8px] bg-[#e5e7eb] border-l rounded-l-none"
                onClick={handleSearch}
              />
            </div>
          </div>

          <div className="px-8 py-4 justify-content-center items-center relative">
              <div className="w-full overflow-x-auto">
            <DataTable
              value={loading === FetchStatusType.LOADING ? [] : listProfil}
              paginator={
                loading === FetchStatusType.COMPLETED &&
                (listProfil.length || 0) > 0
              }
              sortField={order}
              paginatorTemplate={paginatorTemplate}
              rows={pageSize}
              totalRecords={profileResponse?.total}
              rowsPerPageOptions={[10, 20, 50]}
              first={currentPage * pageSize}
              onPage={onPageChange}
              sortOrder={orderDir}
              lazy
              stripedRows
              size="small"
              className="w-full"
              paginatorLeft
              onSort={(e) => {
                setOrder(e.sortField);
                setOrderDir(e.sortOrder ?? 0);
                dispatch(
                  getProfileList({
                    page: currentPage + 1,
                    size: pageSize,
                    sortBy: e.sortField,
                    sortDir: (e.sortOrder ?? 0) === 1 ? "asc" : "desc",
                    search: search,
                  })
                );
              }}
              emptyMessage={
                loading === FetchStatusType.LOADING ? (
                  <div className="flex justify-center py-4">
                    <Spinner />
                  </div>
                ) : (
                  <div className="flex justify-center py-4">
                    Aucune donnée disponible
                  </div>
                )
              }
            >
              <Column
                field="name"
                header="Nom du groupe"
                body={nameBodyTemplate}
                sortable
              />
              <Column
                field="permissions"
                header="Permissions"
                body={permissionBodyTemplate}
                sortable
              />
              <Column
                field="accesses"
                header="Accès"
                body={accessesBodyTemplate}
                sortable
              />
              <Column header="Action" body={actionBodyTemplate} />
            </DataTable>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
